import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { UploadDeDocumentos, UploadDocumentos } from 'src/compartilhados'
import { Accordion, Botao, FileInput, Informacao } from 'src/componentes'
import {
  DataInputUnform,
  FormUnform,
  SelectUnform
} from 'src/componentes/unform'
import { Api } from 'src/servicos'
import { MatriculaDoPainelDoAluno } from 'src/tipos'

import {
  AvisoLicenca,
  CampoFormulario,
  ListaEnviados,
  TagOpcional,
  TituloSecao
} from './secao-licenca-aluno.styles'

interface TurmaPainel {
  turmaCertificadora?: boolean
  nome?: string
}

interface Props {
  matriculas: MatriculaDoPainelDoAluno[]
  documentosEnviados?: UploadDocumentos[]
  eventoDeDownload?: (documentoId: string) => Promise<void>
  eventoDeVisualizacao?: (documentoId: string) => Promise<void>
  aoEnviarSucesso?: () => void
}

const schema = Yup.object().shape({
  tipoLicenca: Yup.string().required('Informe o tipo de licença.'),
  dataNascimentoOuAdocao: Yup.string().required(
    'Informe a data de nascimento ou adoção.'
  ),
  dataInicioAfastamento: Yup.string().required(
    'Informe o início do afastamento.'
  ),
  matriculaId: Yup.string().required('Selecione a turma afetada.'),
  anexo: Yup.mixed()
    .required('Anexe o comprovante.')
    .test(
      'tipo-pdf',
      'O comprovante deve ser um arquivo PDF.',
      arquivo =>
        arquivo instanceof File && arquivo.name.toLowerCase().endsWith('.pdf')
    )
})

export const SecaoLicencaAluno: React.FC<Props> = ({
  matriculas,
  documentosEnviados = [],
  eventoDeDownload,
  eventoDeVisualizacao,
  aoEnviarSucesso
}) => {
  const [enviando, definirEnviando] = useState(false)

  const opcoesTurma = useMemo(
    () =>
      (matriculas ?? [])
        .filter(matricula => {
          const turma = matricula.turma as TurmaPainel | undefined
          return Boolean(turma?.nome) && turma?.turmaCertificadora !== true
        })
        .map(matricula => ({
          id: matricula.id,
          texto: (matricula.turma as TurmaPainel).nome
        })),
    [matriculas]
  )

  const enviar = async (dados: {
    tipoLicenca: string
    dataNascimentoOuAdocao: string
    dataInicioAfastamento: string
    matriculaId: string
    anexo: File
  }) => {
    if (!dados.anexo) {
      toast('Anexe o comprovante da solicitação.', { type: 'error' })
      return
    }

    definirEnviando(true)
    try {
      const form = new FormData()
      form.append('tipoLicenca', dados.tipoLicenca)
      form.append('dataNascimentoOuAdocao', dados.dataNascimentoOuAdocao)
      form.append('dataInicioAfastamento', dados.dataInicioAfastamento)
      form.append('matriculaId', dados.matriculaId)
      form.append('anexo', dados.anexo)

      const ok = await Api.SolicitarLicencaAluno(form)
      if (ok) {
        toast('Solicitação de licença enviada.', { type: 'success' })
        aoEnviarSucesso?.()
      } else {
        toast('Não foi possível enviar a solicitação de licença.', {
          type: 'error'
        })
      }
    } catch {
      toast('Não foi possível enviar a solicitação de licença.', {
        type: 'error'
      })
    } finally {
      definirEnviando(false)
    }
  }

  if (!opcoesTurma.length && documentosEnviados.length === 0) {
    return null
  }

  return (
    <div>
      <TituloSecao>
        Outras solicitações: não fazem parte dos documentos do certificado
        <TagOpcional>Opcional</TagOpcional>
      </TituloSecao>
      {documentosEnviados.length > 0 && (
        <ListaEnviados>
          {documentosEnviados.map(documento => (
            <UploadDeDocumentos
              key={documento.documentoId ?? documento.id}
              documento={documento}
              eventoDeMudanca={() => undefined}
              eventoDeDownload={eventoDeDownload ?? (async () => undefined)}
              eventoDeVisualizacao={
                eventoDeVisualizacao ?? (async () => undefined)
              }
              somenteConsulta
            />
          ))}
        </ListaEnviados>
      )}
      {opcoesTurma.length > 0 && (
      <Accordion
        titulo="Licença maternidade, paternidade ou adoção"
        iniciarAberto={false}
        desativarScrollExpandir
      >
        <AvisoLicenca>
          Esta solicitação suspende prazos e presenças pelo período legal. Não
          tem relação com a emissão do certificado.
        </AvisoLicenca>
        <Informacao>
          Preencha o formulário. Não use a lista de documentos do certificado
          para este envio.
        </Informacao>
        <FormUnform schema={schema} acaoSucesso={enviar}>
          <CampoFormulario>
            <SelectUnform
              id="slct_tipo_licenca"
              name="tipoLicenca"
              label="Tipo"
              obrigatorio
              opcoes={[
                { id: 'Maternidade', texto: 'Maternidade' },
                { id: 'Paternidade', texto: 'Paternidade' },
                { id: 'Adocao', texto: 'Adoção' }
              ]}
            />
          </CampoFormulario>
          <CampoFormulario>
            <DataInputUnform
              name="dataNascimentoOuAdocao"
              label="Data de nascimento ou adoção"
              obrigatorio
            />
          </CampoFormulario>
          <CampoFormulario>
            <DataInputUnform
              name="dataInicioAfastamento"
              label="Início do afastamento"
              obrigatorio
            />
          </CampoFormulario>
          <CampoFormulario>
            <SelectUnform
              id="slct_turma_licenca"
              name="matriculaId"
              label="Turma afetada"
              obrigatorio
              opcoes={opcoesTurma}
            />
          </CampoFormulario>
          <CampoFormulario>
            <FileInput
              name="anexo"
              label="Comprovante"
              required
              accept=".pdf,application/pdf"
            />
          </CampoFormulario>
          <Botao
            texto="Enviar solicitação"
            type="submit"
            carregando={enviando}
          />
        </FormUnform>
      </Accordion>
      )}
    </div>
  )
}
